import React, { useEffect, useMemo, useState } from "react";
import ReactHlsPlayer from "react-hls-player";
import { Link, useParams } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { Api } from "../../services/api";
import { renderDescription } from "../../utils/richText";
import Partners from "../Partners";
import useWakeLock from "../../hooks/useWakeLock";

export default function SessionDetail() {
  const { sessionId } = useParams();
  const { user } = useUser();
  const api = useMemo(() => new Api(), []);
  const [session, setSession] = useState(null);
  const [sessionVideo, setSessionVideo] = useState(null);
  const playerRef = React.useRef();
  const trackedRef = React.useRef(false);

  useWakeLock(playerRef);

  useEffect(() => {
    const trackView = async () => {
      if (user && session && !trackedRef.current) {
        trackedRef.current = true;
        try {
          await api.postWithAuth(
            `/api/session-views`,
            {
              sessionTitle: session.title,
              userId: user.user?.id,
            },
            user,
          );
        } catch (err) {
          console.error("Failed to track session view", err);
          trackedRef.current = false;
        }
      }
    };
    trackView();
  }, [session, user, api]);

  useEffect(() => {
    const getData = async () => {
      const response = await api.getWithAuth(
        `/api/sessions/${sessionId}`,
        user,
      );
      const json = await response.json();
      setSession(json);
    };
    getData();
  }, [sessionId, user, api]);

  useEffect(() => {
    const getVideo = async () => {
      const response = await api.getWithAuth(
        `/api/sessionvideos?where[session][equals]=${sessionId}&where[isPublished][equals]=true`,
        user,
      );
      const json = await response.json();
      if (json.docs && json.docs.length > 0) {
        setSessionVideo(json.docs[0]);
      }
    };
    if (sessionId) getVideo();
  }, [sessionId, user, api]);

  return (
    session && (
      <div className="col-span-full">
        <div className="px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <Link
              to={`/`}
              className="text-blue-600 hover:underline mb-4 inline-block"
            >
              ← Torna alle Sessioni
            </Link>
            <h1 className="text-3xl font-bold mb-4">{session.title}</h1>
            {sessionVideo && (
              <div className="mb-6 w-4/5 mx-auto">
                {(() => {
                  const videoUrl = sessionVideo.vodUrl;
                  const isMP4 = videoUrl?.toLowerCase().endsWith(".mp4");

                  return isMP4 ? (
                    <video
                      ref={playerRef}
                      src={videoUrl}
                      autoPlay={false}
                      loop={true}
                      controls={true}
                      className="w-full block"
                      onContextMenu={(e) => e.preventDefault()}
                      controlsList="nodownload"
                    />
                  ) : (
                    <ReactHlsPlayer
                      playerRef={playerRef}
                      src={videoUrl}
                      autoPlay={false}
                      controls={true}
                      className="w-full block"
                      onContextMenu={(e) => e.preventDefault()}
                      controlsList="nodownload"
                    />
                  );
                })()}
              </div>
            )}
            {session.description && (
              <div className="text-gray-600 mb-6">
                {renderDescription(session.description)}
              </div>
            )}
            <div className="text-sm text-gray-500 mb-6">
              <p>
                <strong>Quando:</strong>{" "}
                {new Date(session.startDateTime).toLocaleDateString("it-IT", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                })}{" "}
                dalle{" "}
                {new Date(session.startDateTime).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}{" "}
                alle{" "}
                {new Date(session.endDateTime).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </div>
        <Partners />
      </div>
    )
  );
}
